import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonDirective } from 'primeng/button';
import { ProgressSpinner } from 'primeng/progressspinner';

import {
    AuthService,
    CustomerContextService,
    SessionService,
    SessionState,
    translatedObjectSignal,
} from '@advania-cosoftware/wolf-core';

import { SessionCardLogoComponent } from '../components/session-card-logo/session-card-logo.component';
import {
    SessionCardActionsDirective,
    SessionCardBodyDirective,
    SessionCardComponent,
    SessionCardHeaderDirective,
} from '../components/session-card/session-card.component';
import { SessionStateText, sessionStateTextKey } from './session-state.translations';

@Component({
    selector: 'app-session-state',
    templateUrl: './session-state.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonDirective,
        ProgressSpinner,
        SessionCardComponent,
        SessionCardHeaderDirective,
        SessionCardBodyDirective,
        SessionCardActionsDirective,
        SessionCardLogoComponent,
    ],
})
export class SessionStateComponent {
    private readonly _auth = inject(AuthService);
    private readonly _customerContext = inject(CustomerContextService);
    private readonly _session = inject(SessionService);
    private readonly _router = inject(Router);

    protected readonly SessionState = SessionState;
    protected readonly sessionState = this._session.state;
    protected readonly text = translatedObjectSignal<SessionStateText>(sessionStateTextKey);

    protected readonly isLoading = computed(() => {
        const state = this._session.state();
        return (
            state === SessionState.LoadingAuthentication ||
            state === SessionState.LoadingCustomerAccess ||
            state === SessionState.LoadingCustomerContext
        );
    });

    protected readonly isContextError = computed(() => this._session.state() === SessionState.CustomerContextError);

    protected retry(): void {
        switch (this._session.state()) {
            case SessionState.CustomerContextError:
                this._customerContext.retryCustomerContext();
                break;
            case SessionState.AuthenticationError:
                this._auth.retry();
                break;
            case SessionState.CustomerAccessError:
            case SessionState.CustomerUnavailable:
                this._customerContext.retryCustomerAccess();
                break;
        }
    }

    protected selectCustomer(): void {
        void this._router.navigate(['/']);
    }
}
